import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { userService, exchangeService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const UserDirectory = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  // Stato modale dettagli abilità
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsUser, setDetailsUser] = useState(null);

  // Stato modale proponi scambio
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [exchangeRequester, setExchangeRequester] = useState('');
  const [exchangeRecipient, setExchangeRecipient] = useState('');
  const [exchangeDescription, setExchangeDescription] = useState('');
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [exchangeError, setExchangeError] = useState('');
  const [exchangeSuccess, setExchangeSuccess] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await userService.getDirectory('it');
        setUsers(res.data || []);
      } catch (e) {
        setError('Errore nel caricamento della lista utenti.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
      const email = (u.email || '').toLowerCase();
      const city = (u.city || '').toLowerCase();
      const skillsText = (u.skills || [])
        .map((s) => `${s.category_label || ''} ${s.subcategory_label || ''} ${s.note || ''}`.trim().toLowerCase())
        .join(' ');
      return fullName.includes(q) || email.includes(q) || city.includes(q) || skillsText.includes(q);
    });
  }, [users, query]);

  const isAdmin = ['admin', 'moderator', 'superadmin'].includes(currentUser?.role);

  // Destinatari selezionabili: tutti tranne se stessi
  const selectableUsers = useMemo(
    () => users.filter((u) => u.id !== currentUser?.id),
    [users, currentUser]
  );

  // Richiedenti selezionabili (solo admin): tutti gli utenti + current user se non già presente
  const allUsers = useMemo(() => {
    if (!currentUser) return users;
    const inList = users.some((u) => u.id === currentUser.id);
    if (inList) return users;
    return [{ id: currentUser.id, first_name: currentUser.first_name, last_name: currentUser.last_name, email: currentUser.email }, ...users];
  }, [users, currentUser]);

  const openDetails = (u) => {
    setDetailsUser(u);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsUser(null);
  };

  const openExchange = (u) => {
    setExchangeRequester(currentUser?.id || '');
    setExchangeRecipient(u.id);
    setExchangeDescription('');
    setExchangeError('');
    setExchangeSuccess('');
    setExchangeOpen(true);
  };

  const closeExchange = () => {
    setExchangeOpen(false);
    setExchangeRequester('');
    setExchangeRecipient('');
    setExchangeDescription('');
    setExchangeError('');
    setExchangeSuccess('');
  };

  const submitExchange = async () => {
    if (!exchangeRecipient) {
      setExchangeError('Seleziona un destinatario.');
      return;
    }
    if (!exchangeDescription.trim()) {
      setExchangeError('Inserisci una descrizione.');
      return;
    }
    setExchangeLoading(true);
    setExchangeError('');
    try {
      const payload = { recipient: exchangeRecipient, description: exchangeDescription.trim() };
      if (isAdmin && exchangeRequester) payload.requester = exchangeRequester;
      await exchangeService.create(payload);
      setExchangeSuccess('Richiesta inviata con successo!');
      setTimeout(closeExchange, 1500);
    } catch (e) {
      setExchangeError('Errore durante l\'invio della richiesta. Riprova.');
    } finally {
      setExchangeLoading(false);
    }
  };

  const getUniqueCategories = (skills) => {
    const map = new Map();
    for (const s of skills || []) {
      const key = s.category !== undefined && s.category !== null ? String(s.category) : (s.category_label || '');
      if (!map.has(key)) map.set(key, s.category_label || '');
    }
    return Array.from(map.values()).filter(Boolean);
  };

  const hasSkills = (u) => (u.skills || []).length > 0;

  return (
    <Container component="main" maxWidth={false} disableGutters>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'center', position: 'relative', mb: 3 }}>
          <Button
            component={Link}
            to="/dashboard"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="primary"
            sx={{ alignSelf: { xs: 'flex-start', sm: 'flex-start' }, mb: { xs: 1, sm: 0 }, textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Typography component="h1" variant="h4" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' }, width: '100%' }}>
            Utenti e abilità
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 3 }}>
          <TextField
            fullWidth
            label="Cerca per nome, email, città o abilità"
            variant="outlined"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: { xs: '100%', md: 320 } }}
          />
          <Button
            variant="outlined"
            onClick={() => setQuery('')}
            sx={{ textTransform: 'none', width: { xs: '100%', sm: 'auto' } }}
          >
            Reset filtri
          </Button>
        </Box>

        {!isMobile ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cognome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Città</TableCell>
                  <TableCell>Abilità</TableCell>
                  <TableCell>Dettagli</TableCell>
                  <TableCell>Proponi scambio</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                        Caricamento…
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length > 0 ? (
                  filteredUsers.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.first_name}</TableCell>
                      <TableCell>{u.last_name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{u.city || '-'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {hasSkills(u) ? (
                            getUniqueCategories(u.skills).map((label) => (
                              <Chip key={label} label={label} variant="outlined" size="small" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">Nessuna abilità</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openDetails(u)}
                          disabled={!hasSkills(u)}
                          sx={{ textTransform: 'none' }}
                        >
                          Dettagli
                        </Button>
                      </TableCell>
                      <TableCell>
                        {hasSkills(u) && u.id !== currentUser?.id && (
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            startIcon={<HandshakeIcon />}
                            onClick={() => openExchange(u)}
                            sx={{ textTransform: 'none' }}
                          >
                            Proponi
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body1" align="center" sx={{ mt: 2 }}>
                        Nessun utente trovato.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ) : loading ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body1" align="center">Caricamento…</Typography>
          </Paper>
        ) : filteredUsers.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
            {filteredUsers.map((u) => (
              <Paper key={u.id} variant="outlined" sx={{ p: 2, width: '100%', minWidth: 0, overflow: 'hidden' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {u.first_name} {u.last_name}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                  {u.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Città: {u.city || '-'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, minWidth: 0 }}>
                  {hasSkills(u) ? (
                    getUniqueCategories(u.skills).map((label) => (
                      <Chip
                        key={label}
                        label={label}
                        variant="outlined"
                        size="small"
                        sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">Nessuna abilità</Typography>
                  )}
                </Box>
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => openDetails(u)}
                    disabled={!hasSkills(u)}
                    sx={{ textTransform: 'none' }}
                  >
                    Dettagli
                  </Button>
                  {hasSkills(u) && u.id !== currentUser?.id && (
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      startIcon={<HandshakeIcon />}
                      onClick={() => openExchange(u)}
                      sx={{ textTransform: 'none' }}
                    >
                      Proponi scambio
                    </Button>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography variant="body1" align="center">Nessun utente trovato.</Typography>
          </Paper>
        )}
      </Paper>

      {/* Modale dettagli abilità */}
      <Dialog open={detailsOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailsUser ? `${detailsUser.first_name || ''} ${detailsUser.last_name || ''}`.trim() : 'Dettagli abilità'}
        </DialogTitle>
        <DialogContent>
          {!isMobile ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Sottocategoria</TableCell>
                    <TableCell>Nota</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(detailsUser?.skills || []).length > 0 ? (
                    (detailsUser.skills || []).map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>{s.category_label || '-'}</TableCell>
                        <TableCell>{s.subcategory_label || '-'}</TableCell>
                        <TableCell>{s.note || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body2" color="text.secondary">Nessuna abilità</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
              {(detailsUser?.skills || []).length > 0 ? (
                (detailsUser.skills || []).map((s) => (
                  <Paper key={s.id} variant="outlined" sx={{ p: 1.5, minWidth: 0, overflow: 'hidden' }}>
                    <Typography variant="caption" color="text.secondary">Categoria</Typography>
                    <Typography variant="body2">{s.category_label || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Sottocategoria</Typography>
                    <Typography variant="body2">{s.subcategory_label || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Nota</Typography>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {s.note || '-'}
                    </Typography>
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">Nessuna abilità</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails} sx={{ textTransform: 'none' }}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Modale proponi scambio */}
      <Dialog open={exchangeOpen} onClose={closeExchange} maxWidth="sm" fullWidth>
        <DialogTitle>Proponi uno scambio</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {exchangeError && <Alert severity="error">{exchangeError}</Alert>}
            {exchangeSuccess && <Alert severity="success">{exchangeSuccess}</Alert>}

            {isAdmin ? (
              <FormControl fullWidth>
                <InputLabel>Richiedente</InputLabel>
                <Select
                  value={exchangeRequester}
                  label="Richiedente"
                  onChange={(e) => setExchangeRequester(e.target.value)}
                >
                  {allUsers.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} — {u.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Richiedente"
                value={currentUser?.email || ''}
                variant="outlined"
                fullWidth
                inputProps={{ readOnly: true, style: { cursor: 'default' } }}
                sx={{ '& .MuiOutlinedInput-root': { pointerEvents: 'none' } }}
              />
            )}

            <FormControl fullWidth>
              <InputLabel>Destinatario</InputLabel>
              <Select
                value={exchangeRecipient}
                label="Destinatario"
                onChange={(e) => setExchangeRecipient(e.target.value)}
              >
                {selectableUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} — {u.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Descrizione"
              multiline
              rows={4}
              value={exchangeDescription}
              onChange={(e) => setExchangeDescription(e.target.value)}
              variant="outlined"
              fullWidth
              placeholder="Descrivi l'attività che vorresti scambiare…"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExchange} sx={{ textTransform: 'none' }} disabled={exchangeLoading}>
            Annulla
          </Button>
          <Button
            onClick={submitExchange}
            variant="contained"
            sx={{ textTransform: 'none' }}
            disabled={exchangeLoading}
            startIcon={exchangeLoading ? <CircularProgress size={16} /> : null}
          >
            Invia richiesta
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserDirectory;
